import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { authService } from '../services/authService'

interface AuthUser {
  id: number
  email: string
  first_name: string
  last_name: string
  role?: string
  agency_id?: number | null
  agency_name?: string | null
  is_applicant: boolean
  email_verified: boolean
  is_primary_contact?: boolean
  enable_subagent_creation?: boolean
  hub_applicant_id?: string
  institution_id?: number | null
  institution_name?: string | null
  // Personal info
  citizenship?: string | null
  secondary_citizenship?: string | null
  city_of_birth?: string | null
  country_of_birth?: string | null
  country_of_residence?: string | null
  country_phone_code?: string | null
  preferred_name?: string | null
  father_name?: string | null
  mother_name?: string | null
  // Address
  address_city?: string | null
  address_country?: string | null
  address_street?: string | null
  address_zip_code?: string | null
  // Contact
  family_email?: string | null
  family_phone_number?: string | null
}

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  setUser: (user: AuthUser | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      if (!localStorage.getItem('access_token')) {
        setIsLoading(false)
        return
      }
      try {
        const userData = await authService.getCurrentUser()
        setUser(userData)
      } catch {
        // Token invalid/expired — interceptor already cleared tokens and will redirect
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, setUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
